// Drawola Studio — "Mindmap" built-in template. Fork-only.
//
// This is a REAL mindmap, not a picture of one: every rectangle carries the
// fork's `customData.drawolaMindmap` payload (see excalidraw-app/mindmap/),
// so the created draw is immediately driveable by the interactive mindmap
// feature — the "+" handles appear on hover, new children and siblings attach
// to the tree, deleting a node cascades to its subtree, and re-parenting works.
//
// How it is built, and why it is built this way:
//
//  - The fork's own node builders (`insertRootMindmapBlock`,
//    `addChildMindmapNode` in mindmap/drawolaMindmapElement.ts) all take an
//    `ExcalidrawImperativeAPI`, call `api.updateScene()` and then
//    `focusNewMindmapNodeForEditing()` — i.e. they drive the live editor and
//    open the inline text editor. The Studio has no editor API at the moment a
//    template is picked, so they are not reusable here, and their pure core
//    (`buildConnectedMindmapNode`) is module-private.
//
//  - So the NODES are authored as skeletons for upstream's public
//    `convertToExcalidrawElements` (ids, seeds, versions, fractional indices,
//    bound labels and arrow<->shape bindings all produced by the library), and
//    the two pieces of mindmap-specific structure are layered on top from the
//    fork's own modules:
//      1. the `customData` payload + node sizes from mindmap/types.ts,
//      2. the canonical left/right edge anchors, and the final geometry from
//         `applyMindmapLayout` (mindmap/drawolaMindmapLayout.ts) — the exact
//         function the feature runs after every insertion, so the template
//         opens in the same shape the feature would have produced and nothing
//         jumps on the first interaction.

import { randomId, ROUNDNESS } from "@excalidraw/common";
import { newElementWith } from "@excalidraw/element";
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element/transform";
import type {
  ExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";

import { applyMindmapLayout } from "../../mindmap/drawolaMindmapLayout";
import { isMindmapNode } from "../../mindmap/drawolaMindmapModel";
import {
  MINDMAP_CHILD_HEIGHT,
  MINDMAP_CHILD_WIDTH,
  MINDMAP_DATA_KEY,
  MINDMAP_HORIZONTAL_GAP,
  MINDMAP_ROOT_HEIGHT,
  MINDMAP_ROOT_WIDTH,
  MINDMAP_VERTICAL_GAP,
} from "../../mindmap/types";

import { templateAppState } from "./shared";

import type { MindmapNodeElement } from "../../mindmap/drawolaMindmapModel";
import type { MindmapNodeData, MindmapSide } from "../../mindmap/types";
import type { TemplateScene } from "./types";

// Mirrors the (module-private) constants in mindmap/drawolaMindmapElement.ts
// so a templated mindmap is indistinguishable from a hand-built one: Material
// Blue 100 for the root, transparent for the branches.
const ROOT_BACKGROUND = "#bbdefb";
const CHILD_BACKGROUND = "transparent";
const ROOT_FONT_SIZE = 20;
const CHILD_FONT_SIZE = 16;

const ROUNDED = { type: ROUNDNESS.PROPORTIONAL_RADIUS } as const;

// Chosen so the laid-out tree spans roughly x 60..1140, y 268..412 — i.e. it
// lands inside the viewport of an editor sitting at its default scroll origin
// (`switchTo()` loads elements without re-centring the canvas).
const ROOT_X = 340;
const ROOT_Y = 300;

type Branch = {
  text: string;
  children?: string[];
};

// Grandchildren only on the right, so the tree stays one column wide on each
// side of the root and does not run off the left edge of the canvas.
const RIGHT_BRANCHES: Branch[] = [
  { text: "Goals", children: ["Success metrics"] },
  { text: "Timeline", children: ["Milestones"] },
];

const LEFT_BRANCHES: Branch[] = [{ text: "Team" }, { text: "Risks" }];

/** Stack `count` child boxes vertically, centred on the root's centre line. */
const stackY = (index: number, count: number): number => {
  const step = MINDMAP_CHILD_HEIGHT + MINDMAP_VERTICAL_GAP;
  const centreY = ROOT_Y + MINDMAP_ROOT_HEIGHT / 2;
  return centreY + (index - (count - 1) / 2) * step - MINDMAP_CHILD_HEIGHT / 2;
};

/**
 * A parent -> child connector. Arrowhead-free and non-elbowed, matching what
 * `addChildMindmapNode` produces.
 */
const edge = (
  fromId: string,
  toId: string,
  side: "L" | "R",
): ExcalidrawElementSkeleton => ({
  type: "arrow",
  // Placeholder geometry: `anchorEdges` pins the bindings and
  // `applyMindmapLayout` then rewrites x/y/points from them.
  x: ROOT_X,
  y: ROOT_Y,
  width: side === "R" ? MINDMAP_HORIZONTAL_GAP : -MINDMAP_HORIZONTAL_GAP,
  height: 0,
  startArrowhead: null,
  endArrowhead: null,
  start: { id: fromId },
  end: { id: toId },
});

/**
 * Pin every mindmap connector to the canonical side anchors.
 *
 * `convertToExcalidrawElements` derives a fixed point geometrically, from
 * wherever the placeholder arrow happened to sit. The feature instead always
 * uses the parent's and the child's mid-edge — right->left for a right-side
 * branch, left->right for a left-side one — and `applyMindmapLayout` reads
 * those fixed points literally when it re-routes an edge. Overwriting them
 * here is what makes the template's arrows behave like feature-made ones.
 */
const anchorEdges = (
  elements: readonly OrderedExcalidrawElement[],
): readonly OrderedExcalidrawElement[] => {
  const byId = new Map(elements.map((element) => [element.id, element]));

  return elements.map((element) => {
    if (
      element.type !== "arrow" ||
      !element.startBinding ||
      !element.endBinding
    ) {
      return element;
    }
    const child = byId.get(element.endBinding.elementId);
    if (!child || !isMindmapNode(child)) {
      return element;
    }
    const side = child.customData[MINDMAP_DATA_KEY].side;
    if (side !== "L" && side !== "R") {
      return element;
    }
    const startFP: [number, number] = side === "R" ? [1, 0.5] : [0, 0.5];
    const endFP: [number, number] = side === "R" ? [0, 0.5] : [1, 0.5];

    return newElementWith(element, {
      startBinding: { ...element.startBinding, fixedPoint: startFP },
      endBinding: { ...element.endBinding, fixedPoint: endFP },
    });
  });
};

/** Run the feature's own layout so the template opens perfectly aligned. */
const layOut = (
  elements: readonly OrderedExcalidrawElement[],
): readonly OrderedExcalidrawElement[] => {
  const root = elements.find(
    (element): element is OrderedExcalidrawElement & MindmapNodeElement =>
      isMindmapNode(element) &&
      element.customData[MINDMAP_DATA_KEY].parentId === null,
  );
  if (!root) {
    return elements;
  }
  // `applyMindmapLayout` only ever rewrites x/y/points through
  // `newElementWith`, which preserves `index`, so the ordered branding holds.
  return applyMindmapLayout(
    elements as readonly ExcalidrawElement[],
    root,
  ) as OrderedExcalidrawElement[];
};

export const buildMindmapTemplate = (): TemplateScene => {
  // One mindmap id per build, so two draws created from this template never
  // look like two halves of the same tree if their contents are ever pasted
  // together.
  const mindmapId = randomId();

  const nodeData = (
    nodeId: string,
    parentId: string | null,
    side: MindmapSide,
  ): { [MINDMAP_DATA_KEY]: MindmapNodeData } => ({
    [MINDMAP_DATA_KEY]: {
      __drawolaKind: "mindmapNode",
      mindmapId,
      nodeId,
      parentId,
      side,
    },
  });

  const rootNodeId = randomId();
  const nodes: ExcalidrawElementSkeleton[] = [
    {
      type: "rectangle",
      id: "root",
      x: ROOT_X,
      y: ROOT_Y,
      width: MINDMAP_ROOT_WIDTH,
      height: MINDMAP_ROOT_HEIGHT,
      backgroundColor: ROOT_BACKGROUND,
      fillStyle: "solid",
      roundness: ROUNDED,
      customData: nodeData(rootNodeId, null, null),
      label: { text: "Project kickoff", fontSize: ROOT_FONT_SIZE },
    },
  ];
  const edges: ExcalidrawElementSkeleton[] = [];

  const addBranches = (branches: Branch[], side: "L" | "R") => {
    const direction = side === "R" ? 1 : -1;

    branches.forEach((branch, index) => {
      const branchKey = `${side}${index}`;
      const branchNodeId = randomId();
      // Rough placement only — `layOut` recomputes every position below.
      const branchX =
        side === "R"
          ? ROOT_X + MINDMAP_ROOT_WIDTH + MINDMAP_HORIZONTAL_GAP
          : ROOT_X - MINDMAP_HORIZONTAL_GAP - MINDMAP_CHILD_WIDTH;
      const branchY = stackY(index, branches.length);

      nodes.push({
        type: "rectangle",
        id: branchKey,
        x: branchX,
        y: branchY,
        width: MINDMAP_CHILD_WIDTH,
        height: MINDMAP_CHILD_HEIGHT,
        backgroundColor: CHILD_BACKGROUND,
        fillStyle: "solid",
        roundness: ROUNDED,
        customData: nodeData(branchNodeId, rootNodeId, side),
        label: { text: branch.text, fontSize: CHILD_FONT_SIZE },
      });
      edges.push(edge("root", branchKey, side));

      branch.children?.forEach((text, childIndex) => {
        const leafKey = `${branchKey}-${childIndex}`;
        nodes.push({
          type: "rectangle",
          id: leafKey,
          x:
            branchX +
            direction * (MINDMAP_CHILD_WIDTH + MINDMAP_HORIZONTAL_GAP),
          y: branchY,
          width: MINDMAP_CHILD_WIDTH,
          height: MINDMAP_CHILD_HEIGHT,
          backgroundColor: CHILD_BACKGROUND,
          fillStyle: "solid",
          roundness: ROUNDED,
          customData: nodeData(randomId(), branchNodeId, side),
          label: { text, fontSize: CHILD_FONT_SIZE },
        });
        edges.push(edge(branchKey, leafKey, side));
      });
    });
  };

  addBranches(RIGHT_BRANCHES, "R");
  addBranches(LEFT_BRANCHES, "L");

  // Nodes first, then edges: `convertToExcalidrawElements` resolves an arrow's
  // `start`/`end` against elements it has already created.
  return {
    elements: layOut(
      anchorEdges(convertToExcalidrawElements([...nodes, ...edges])),
    ),
    appState: templateAppState(),
  };
};
